import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, useTheme, lighten } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import EventBusyIcon from "@material-ui/icons/EventBusy";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewListIcon from "@material-ui/icons/ViewList";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import ptBR from "date-fns/locale/pt-BR";
import "react-big-calendar/lib/css/react-big-calendar.css";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const calendarMessages = {
  next: "Próximo",
  previous: "Anterior",
  today: "Hoje",
  month: "Mês",
  week: "Semana",
  day: "Dia",
  agenda: "Agenda",
  date: "Data",
  time: "Hora",
  event: "Agendamento",
  noEventsInRange: "Nenhum agendamento neste período.",
  showMore: (total) => `+${total} mais`,
  allDay: "Dia todo",
};

const reducer = (state, action) => {
  if (action.type === "LOAD_APPOINTMENTS") {
    const appointments = action.payload;
    const newItems = [];
    appointments.forEach((appt) => {
      const idx = state.findIndex((a) => a.id === appt.id);
      if (idx !== -1) {
        state[idx] = appt;
      } else {
        newItems.push(appt);
      }
    });
    return [...state, ...newItems];
  }

  if (action.type === "UPDATE_APPOINTMENT") {
    const appt = action.payload;
    const idx = state.findIndex((a) => a.id === appt.id);
    if (idx !== -1) {
      state[idx] = appt;
      return [...state];
    }
    return [appt, ...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  "@global": {
    // A lib usa fundo branco fixo por padrão no popup "+X mais" — sem isso,
    // no tema escuro o popup fica claro e os cards (com cor pensada p/ o
    // grid escuro) ficam com texto ilegível.
    ".rbc-overlay": {
      zIndex: "1400 !important",
      backgroundColor: `${theme.palette.background.paper} !important`,
      color: `${theme.palette.text.primary} !important`,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "10px !important",
      boxShadow: "0 8px 32px rgba(0,0,0,0.24) !important",
    },
    ".rbc-overlay-header": {
      fontWeight: "600 !important",
      color: `${theme.palette.text.primary} !important`,
      borderBottomColor: `${theme.palette.divider} !important`,
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  calendarPaper: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  calendarWrapper: {
    flex: 1,
    // Piso mínimo só para não ficar ilegível em janelas muito pequenas — o
    // <Calendar> abaixo usa height:"100%" para ocupar o espaço disponível
    // em vez de uma altura fixa, evitando a barra de rolagem permanente.
    minHeight: 420,
    "& .rbc-toolbar": {
      flexWrap: "wrap",
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
    "& .rbc-toolbar button": {
      borderRadius: 6,
      padding: "4px 10px",
      fontSize: "0.8rem",
      cursor: "pointer",
      color: theme.palette.text.primary,
      background: "transparent",
      borderColor: theme.palette.divider,
    },
    "& .rbc-toolbar button:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button:focus": {
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button.rbc-active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
    "& .rbc-toolbar button.rbc-active:hover": {
      backgroundColor: theme.palette.primary.dark,
      color: "#fff",
    },
    "& .rbc-toolbar-label": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    "& .rbc-event": {
      borderRadius: 4,
      padding: "1px 5px",
    },
    // No mês, deixa o evento crescer em altura para caber o card de 3 linhas
    // (horário / nome / serviço). Em semana e dia a altura segue a duração.
    "& .rbc-month-view .rbc-event": {
      height: "auto",
      padding: "2px 6px",
      whiteSpace: "normal",
    },
    "& .rbc-month-view .rbc-event-content": {
      whiteSpace: "normal",
    },
    "& .rbc-month-view .rbc-row-segment": {
      padding: "0 2px 2px",
    },
    "& .rbc-show-more": {
      // Azul do primary é pouco legível sobre o fundo escuro do calendário
      // — no dark mode usamos uma variação mais clara da mesma cor.
      color:
        theme.palette.type === "dark"
          ? lighten(theme.palette.primary.main, 0.45)
          : theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "0.75rem",
    },
    "& .rbc-off-range-bg": {
      backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
    },
    "& .rbc-today": {
      backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e8f4fd",
    },
    "& .rbc-header": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.primary,
      padding: "6px 4px",
      fontWeight: 600,
    },
    "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
      borderColor: theme.palette.divider,
      borderRadius: 8,
      overflow: "hidden",
    },
    "& .rbc-day-bg + .rbc-day-bg": { borderColor: theme.palette.divider },
    "& .rbc-month-row + .rbc-month-row": { borderColor: theme.palette.divider },
    "& .rbc-header + .rbc-header": { borderColor: theme.palette.divider },
    "& .rbc-time-content, & .rbc-time-header-content": { borderColor: theme.palette.divider },
    "& .rbc-label, & .rbc-time-slot": { color: theme.palette.text.secondary, fontSize: "0.75rem" },
    "& .rbc-date-cell": { color: theme.palette.text.primary },
    "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell, & .rbc-agenda-event-cell": {
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    "& .rbc-agenda-table": { borderColor: theme.palette.divider },
    "& .rbc-agenda-table thead tr th": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },
  },
  legendRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
    alignItems: "center",
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  eventCard: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
    overflow: "hidden",
  },
  eventTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  eventTime: {
    fontWeight: 700,
    fontSize: "0.72rem",
    flexShrink: 0,
  },
  eventIcon: {
    fontSize: 13,
    flexShrink: 0,
  },
  eventName: {
    fontWeight: 600,
    fontSize: "0.72rem",
    flex: "1 1 auto",
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  eventService: {
    fontSize: "0.68rem",
    opacity: 0.85,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const STATUS_META = {
  pendente:   { label: "Pendente",   color: "#ff9800" },
  confirmado: { label: "Confirmado", color: "#2196f3" },
  concluido:  { label: "Concluído",  color: "#4caf50" },
  faltou:     { label: "Faltou",     color: "#f44336" },
  cancelado:  { label: "Cancelado",  color: "#9e9e9e" },
};

// Espelha a mesma matriz do backend (UpdateAppointmentService).
// "concluido", "faltou" e "cancelado" são terminais — sem transições.
const STATUS_TRANSITIONS = {
  pendente:   ["confirmado", "concluido", "faltou", "cancelado"],
  confirmado: ["pendente",   "concluido", "faltou", "cancelado"],
  concluido:  [],
  faltou:     [],
  cancelado:  [],
};

const canTransitionTo = (currentStatus, targetStatus) =>
  (STATUS_TRANSITIONS[currentStatus] || []).includes(targetStatus);

const TERMINAL_STATUSES = ["concluido", "faltou", "cancelado"];

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: "#9e9e9e" };
  return (
    <Chip
      size="small"
      label={meta.label}
      style={{ backgroundColor: meta.color, color: "#fff", fontWeight: 600 }}
    />
  );
};

// Converte a cor hex do status num fundo claro (estilo "card" do calendário).
const hexToRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Appointments = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [appointments, dispatch] = useReducer(reducer, []);
  const [viewMode, setViewMode] = useState("calendar");
  const [statusFilter, setStatusFilter] = useState("");
  const isAdmin = user.profile === "admin";
  const [providerFilter, setProviderFilter] = useState(isAdmin ? "" : String(user.id));
  const [providers, setProviders] = useState([]);

  // Intervalo da LISTA (relatório por período).
  const [dateFrom, setDateFrom] = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  // Intervalo visível do CALENDÁRIO (atualizado ao navegar entre meses/semanas).
  const [calFrom, setCalFrom] = useState(
    moment().startOf("month").startOf("week").format("YYYY-MM-DD")
  );
  const [calTo, setCalTo] = useState(
    moment().endOf("month").endOf("week").format("YYYY-MM-DD")
  );

  const [confirmModal, setConfirmModal] = useState({ open: false, appt: null, status: null });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    // Profissionais para o filtro (mesma fonte usada no cadastro de serviços).
    api
      .get("/users/list")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.users || [];
        setProviders(list.filter((u) => u.isServiceProvider));
      })
      .catch(() => {});
  }, []);

  const fetchAppointments = useCallback(async () => {
    try {
      const isCal = viewMode === "calendar";
      const from = isCal ? calFrom : dateFrom;
      const to = isCal ? calTo : dateTo;

      // Pagina (50/página no backend) até trazer tudo do intervalo visível.
      let page = 1;
      let all = [];
      let more = true;
      while (more && page <= 40) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/appointments", {
          params: {
            dateFrom: from,
            dateTo: to,
            status: statusFilter || undefined,
            userId: providerFilter || undefined,
            pageNumber: page,
          },
        });
        all = all.concat(data.appointments);
        more = data.hasMore;
        page += 1;
      }

      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_APPOINTMENTS", payload: all });
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [viewMode, calFrom, calTo, dateFrom, dateTo, statusFilter, providerFilter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAppointments();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchAppointments]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("appointment", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_APPOINTMENT", payload: data.appointment });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  const updateStatus = async (appt, status) => {
    try {
      await api.put(`/appointments/${appt.id}`, { status });
      toast.success("Agendamento atualizado.");
      dispatch({ type: "UPDATE_APPOINTMENT", payload: { ...appt, status } });
    } catch (err) {
      toastError(err);
    }
    setConfirmModal({ open: false, appt: null, status: null });
    setDetail(null);
  };

  const askConfirm = (appt, status) => {
    setConfirmModal({ open: true, appt, status });
  };

  const confirmLabel = () => {
    if (!confirmModal.status) return "";
    return STATUS_META[confirmModal.status]?.label || confirmModal.status;
  };

  // Eventos do calendário: usa start/end reais do agendamento.
  const calendarEvents = useMemo(
    () =>
      appointments.map((appt) => ({
        id: appt.id,
        title: `${appt.contact ? appt.contact.name : "—"}${
          appt.service ? ` · ${appt.service.name}` : ""
        }`,
        start: new Date(appt.startAt),
        end: new Date(appt.endAt),
        resource: appt,
      })),
    [appointments]
  );

  const eventStyleGetter = useCallback(
    (event) => {
      const meta = STATUS_META[event.resource?.status] || { color: "#9e9e9e" };
      return {
        style: {
          // Card claro com faixa lateral colorida (estilo belasis), em vez de
          // bloco totalmente preenchido — facilita ler horário/nome/serviço.
          backgroundColor: hexToRgba(meta.color, theme.palette.type === "dark" ? 0.22 : 0.12),
          borderLeft: `3px solid ${meta.color}`,
          borderTop: "none",
          borderRight: "none",
          borderBottom: "none",
          borderRadius: 4,
          color: theme.palette.text.primary,
          fontSize: "0.75rem",
        },
      };
    },
    [theme]
  );

  // Card do evento no estilo belasis: horário em destaque, ícone + nome do
  // cliente e, abaixo, o serviço. Usado em mês/semana/dia do calendário.
  const AppointmentEvent = useCallback(
    ({ event }) => {
      const appt = event.resource || {};
      const meta = STATUS_META[appt.status] || { color: "#9e9e9e" };
      return (
        <div className={classes.eventCard}>
          <span className={classes.eventTopRow}>
            <span className={classes.eventTime} style={{ color: meta.color }}>
              {moment(appt.startAt).format("HH:mm")}
            </span>
            <PersonOutlineIcon className={classes.eventIcon} style={{ color: meta.color }} />
            <span className={classes.eventName}>
              {appt.contact ? appt.contact.name : "—"}
            </span>
          </span>
          {appt.service && (
            <span className={classes.eventService}>{appt.service.name}</span>
          )}
        </div>
      );
    },
    [classes]
  );

  const handleRangeChange = (range) => {
    let start;
    let end;
    if (Array.isArray(range)) {
      start = range[0];
      end = range[range.length - 1];
    } else {
      start = range.start;
      end = range.end;
    }
    setCalFrom(moment(start).format("YYYY-MM-DD"));
    setCalTo(moment(end).format("YYYY-MM-DD"));
  };

  const handleSelectEvent = (event) => setDetail(event.resource);

  return (
    <MainContainer>
      <ConfirmationModal
        title="Atualizar agendamento"
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, appt: null, status: null })}
        onConfirm={() => updateStatus(confirmModal.appt, confirmModal.status)}
      >
        {`Marcar este agendamento como "${confirmLabel()}"?`}
      </ConfirmationModal>

      {/* Detalhes do agendamento (clique no evento do calendário) */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="xs" fullWidth>
        {detail && (
          <>
            <DialogTitle>Agendamento</DialogTitle>
            <DialogContent dividers>
              <Box className={classes.detailRow}>
                <Typography variant="body2" color="textSecondary">Cliente</Typography>
                <Typography variant="body2">{detail.contact ? detail.contact.name : "—"}</Typography>
              </Box>
              {detail.contact?.number && (
                <Box className={classes.detailRow}>
                  <Typography variant="body2" color="textSecondary">WhatsApp</Typography>
                  <Typography variant="body2">{detail.contact.number}</Typography>
                </Box>
              )}
              <Box className={classes.detailRow}>
                <Typography variant="body2" color="textSecondary">Serviço</Typography>
                <Typography variant="body2">{detail.service ? detail.service.name : "—"}</Typography>
              </Box>
              <Box className={classes.detailRow}>
                <Typography variant="body2" color="textSecondary">Profissional</Typography>
                <Typography variant="body2">{detail.user ? detail.user.name : "—"}</Typography>
              </Box>
              <Box className={classes.detailRow}>
                <Typography variant="body2" color="textSecondary">Horário</Typography>
                <Typography variant="body2">
                  {moment(detail.startAt).format("DD/MM/YYYY HH:mm")}
                  {detail.endAt ? ` – ${moment(detail.endAt).format("HH:mm")}` : ""}
                </Typography>
              </Box>
              <Box className={classes.detailRow}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <StatusChip status={detail.status} />
              </Box>
            </DialogContent>
            {TERMINAL_STATUSES.includes(detail.status) && (
              <Box px={2} pb={1}>
                <Typography variant="caption" color="textSecondary">
                  Este agendamento está{" "}
                  <strong>{STATUS_META[detail.status]?.label?.toLowerCase()}</strong> e não pode
                  ser alterado.
                </Typography>
              </Box>
            )}
            <DialogActions>
              <Tooltip
                title={
                  canTransitionTo(detail.status, "concluido")
                    ? "Marcar como concluído"
                    : `Não disponível — agendamento está ${STATUS_META[detail.status]?.label?.toLowerCase()}`
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={!canTransitionTo(detail.status, "concluido")}
                    startIcon={
                      <CheckCircleOutlineIcon
                        style={{ color: canTransitionTo(detail.status, "concluido") ? "#4caf50" : undefined }}
                      />
                    }
                    onClick={() => askConfirm(detail, "concluido")}
                  >
                    Concluído
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  canTransitionTo(detail.status, "faltou")
                    ? "Marcar como faltou"
                    : `Não disponível — agendamento está ${STATUS_META[detail.status]?.label?.toLowerCase()}`
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={!canTransitionTo(detail.status, "faltou")}
                    startIcon={
                      <RemoveCircleOutlineIcon
                        style={{ color: canTransitionTo(detail.status, "faltou") ? "#f44336" : undefined }}
                      />
                    }
                    onClick={() => askConfirm(detail, "faltou")}
                  >
                    Faltou
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  canTransitionTo(detail.status, "cancelado")
                    ? "Cancelar agendamento"
                    : `Não disponível — agendamento está ${STATUS_META[detail.status]?.label?.toLowerCase()}`
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={!canTransitionTo(detail.status, "cancelado")}
                    startIcon={
                      <CancelIcon
                        style={{ color: canTransitionTo(detail.status, "cancelado") ? "#9e9e9e" : undefined }}
                      />
                    }
                    onClick={() => askConfirm(detail, "cancelado")}
                  >
                    Cancelar
                  </Button>
                </span>
              </Tooltip>
            </DialogActions>
          </>
        )}
      </Dialog>

      <MainHeader>
        <Title>Agenda</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filters}>
            {viewMode === "list" && (
              <>
                <TextField
                  label="De"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  label="Até"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </>
            )}
            {isAdmin && (
              <FormControl size="small" style={{ minWidth: 150 }}>
                <InputLabel>Profissional</InputLabel>
                <Select
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                  label="Profissional"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" style={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.keys(STATUS_META).map((key) => (
                  <MenuItem key={key} value={key}>
                    {STATUS_META[key].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="calendar">
                <Tooltip title="Calendário">
                  <CalendarTodayIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list">
                <Tooltip title="Lista">
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {viewMode === "calendar" ? (
        <Paper className={classes.calendarPaper} variant="outlined">
          <div className={classes.calendarWrapper}>
            <Calendar
              localizer={localizer}
              culture="pt-BR"
              messages={calendarMessages}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
              onSelectEvent={handleSelectEvent}
              onRangeChange={handleRangeChange}
              eventPropGetter={eventStyleGetter}
              components={{ event: AppointmentEvent }}
              views={["month", "week", "day", "agenda"]}
              defaultView="month"
              min={new Date(0, 0, 0, 8, 0, 0)}
              max={new Date(0, 0, 0, 22, 0, 0)}
              scrollToTime={new Date(0, 0, 0, 8, 0, 0)}
              popup
            />
          </div>
          <Box className={classes.legendRow}>
            <Typography variant="caption" color="textSecondary">
              Legenda:
            </Typography>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <Box key={key} display="flex" alignItems="center" style={{ gap: 6 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    backgroundColor: meta.color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  {meta.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.mainPaper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">Horário</TableCell>
                <TableCell align="left">Cliente</TableCell>
                <TableCell align="left">Serviço</TableCell>
                <TableCell align="left">Profissional</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {!loading && appointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box className={classes.emptyState}>
                        <EventBusyIcon className={classes.emptyIcon} />
                        <Typography variant="body2">
                          Nenhum agendamento no período selecionado.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {appointments.map((appt) => (
                  <TableRow key={appt.id} hover>
                    <TableCell align="left">
                      {moment(appt.startAt).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell align="left">
                      {appt.contact ? appt.contact.name : "—"}
                      {appt.contact && appt.contact.number && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {appt.contact.number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {appt.service ? appt.service.name : "—"}
                    </TableCell>
                    <TableCell align="left">{appt.user ? appt.user.name : "—"}</TableCell>
                    <TableCell align="center">
                      <StatusChip status={appt.status} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          canTransitionTo(appt.status, "concluido")
                            ? "Marcar como concluído"
                            : `Não disponível — ${STATUS_META[appt.status]?.label?.toLowerCase()}`
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canTransitionTo(appt.status, "concluido")}
                            onClick={() => askConfirm(appt, "concluido")}
                          >
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              style={{ color: canTransitionTo(appt.status, "concluido") ? "#4caf50" : undefined }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          canTransitionTo(appt.status, "faltou")
                            ? "Marcar como faltou"
                            : `Não disponível — ${STATUS_META[appt.status]?.label?.toLowerCase()}`
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canTransitionTo(appt.status, "faltou")}
                            onClick={() => askConfirm(appt, "faltou")}
                          >
                            <RemoveCircleOutlineIcon
                              fontSize="small"
                              style={{ color: canTransitionTo(appt.status, "faltou") ? "#f44336" : undefined }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          canTransitionTo(appt.status, "cancelado")
                            ? "Cancelar agendamento"
                            : `Não disponível — ${STATUS_META[appt.status]?.label?.toLowerCase()}`
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canTransitionTo(appt.status, "cancelado")}
                            onClick={() => askConfirm(appt, "cancelado")}
                          >
                            <CancelIcon
                              fontSize="small"
                              style={{ color: canTransitionTo(appt.status, "cancelado") ? "#9e9e9e" : undefined }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={6} />}
              </>
            </TableBody>
          </Table>
        </Paper>
      )}
    </MainContainer>
  );
};

export default Appointments;
